import { AlertTriangle, Home, ShieldAlert } from "lucide-react";
import { defineMessages, useIntl } from "react-intl";

import { Button } from "iqf-web-ui/button";
import { Icon } from "iqf-web-ui/icon";

type PageErrorStatus = 401 | 403 | 404 | 500;

type PageErrorProps = {
  status: PageErrorStatus;
};

const messages = defineMessages({
  unauthorizedTitle: {
    id: "page-error.401.title",
    defaultMessage: "přihlášení je vyžadováno",
  },
  unauthorizedDescription: {
    id: "page-error.401.description",
    defaultMessage: "Pro zobrazení této stránky se musíte přihlásit.",
  },
  forbiddenTitle: {
    id: "page-error.403.title",
    defaultMessage: "přístup odepřen",
  },
  forbiddenDescription: {
    id: "page-error.403.description",
    defaultMessage: "Nemáte dostatečná oprávnění pro zobrazení této stránky.",
  },
  notFoundTitle: {
    id: "page-404.text.title",
    defaultMessage: "stránka nebyla nalezena",
  },
  notFoundDescription: {
    id: "page-404.text.description",
    defaultMessage:
      "Omlouváme se, ale tato stránka nebyla nalezena. Zkontrolujte, jestli máte správnou URL adresu.",
  },
  internalErrorTitle: {
    id: "page-error.500.title",
    defaultMessage: "přihlášení se nepodařilo dokončit",
  },
  internalErrorDescription: {
    id: "page-error.500.description",
    defaultMessage: "Zkuste přihlášení spustit znovu.",
  },
  homeButton: {
    id: "page-error.button.home",
    defaultMessage: "Zpět na nástěnku",
  },
});

const pageErrorContent = {
  401: {
    icon: ShieldAlert,
    title: messages.unauthorizedTitle,
    description: messages.unauthorizedDescription,
  },
  403: {
    icon: ShieldAlert,
    title: messages.forbiddenTitle,
    description: messages.forbiddenDescription,
  },
  404: {
    icon: AlertTriangle,
    title: messages.notFoundTitle,
    description: messages.notFoundDescription,
  },
  500: {
    icon: AlertTriangle,
    title: messages.internalErrorTitle,
    description: messages.internalErrorDescription,
  },
} satisfies Record<PageErrorStatus, object>;

export function PageError({ status }: PageErrorProps) {
  const intl = useIntl();
  const content = pageErrorContent[status];

  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="max-w-md text-center">
        <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-red-100">
          <Icon Icon={content.icon} className="size-12 text-primary" />
        </div>

        <h1 className="mb-4 text-4xl font-bold text-gray-900">{status}</h1>
        <h2 className="mb-4 text-xl font-semibold text-gray-800">
          {intl.formatMessage(content.title)}
        </h2>
        <p className="mb-8 leading-relaxed text-gray-600">
          {intl.formatMessage(content.description)}
        </p>

        <div className="space-y-3">
          <Button
            variant="outlined"
            className="w-full bg-transparent"
            href="/"
            iconLeft={{ Icon: Home }}
          >
            {intl.formatMessage(messages.homeButton)}
          </Button>
        </div>
      </div>
    </div>
  );
}
